import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { ButtonToolbar, Button, Col, Row, Panel, ButtonGroup } from 'react-bootstrap';

import { FileImporter } from "./helpers/FileImporter";
import { SearchHelper } from '../helpers/SearchHelper';

import { TitleRow } from '../TitleRow';
import { HandleDialog } from "../HandleDialog";
import { HandleMessage } from '../HandleMessage';
import { LoadingSpinner } from '../LoadingSpinner';

import { MapSummary } from "./generation/MapSummary";
import { LineSelection } from "./generation/LineSelection";
import { PathSelection } from "./generation/PathSelection";
import { PointSelection } from "./generation/PointSelection";
import { LineColorSelection } from "./generation/LineColorSelection";
import { GenericSelectableTable } from "./generation/GenericSelectableTable";

import { DrawCommand } from "./commands/DrawCommand";
import { LinesCommand } from './commands/LinesCommand';
import { PathsCommand } from './commands/PathsCommand';
import { PointsCommand } from "./commands/PointsCommand";


import { StepConfigurationHelper } from "./helpers/StepConfigurationHelper";
import { LayoutCommand } from "../genericLayoutConfigurations/commands/LayoutCommand";

export class SchematicMap extends Component {
    static contextTypes = {
        selectApp: PropTypes.func,
        getOwnEdition: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.fileImporterRef = React.createRef();

        this.state = {
            edition: undefined,
            commands: {
                lines: new LinesCommand(),
                paths: new PathsCommand(),
                points: new PointsCommand(),
                draw: new DrawCommand(),
                layouts: new LayoutCommand("schematicmap")
            },
            form: {
                layout: undefined,
                referenceDate: undefined,
                lines: [],
                pointType: 0,
                paths: [],
                points: [],
                mapPoint: undefined
            },
            data: {
                layouts: [],
                lines: [],
                paths: [],
                points: [],
                mapPointsFilter: []
            },
            render: {
                filter: "",
                result: undefined
            },
            actions: {
                isLoadingEdition: true,
                isLoadingLayouts: true,
                isLoadingLines: true,
                isLoadingPaths: true,
                isLoadingPoints: true,
                isLoadingMapPoints: true,
                isLoadingShapes: true,
                isGenerating: false,
                isDownloading: false,
                isImporting: false,
                remainingBatches: 0
            },
            message: {
                show: false,
                message: undefined,
                style: undefined,
                icon: undefined,
                title: ""
            },
            dialog: {
                show: false,
                title: undefined,
                message: undefined,
                style: undefined,
                icon: undefined,
                argument: undefined
            },
            step: 0,
            substep: null,
            usePriorityPaths: false,
            showShapeWarning: false,
            shouldAggregatePaths: false,
            useAllLinesInCaption: false,
        };
    }

    componentDidMount() {
        this.context.selectApp("schematicmap", false);

        const { form } = this.state
        form.referenceDate = new Date().toISOString();

        this.getLayouts();

        const edition = this.context.getOwnEdition();
        edition.addObserver(this);
        edition.updateSync(new Date())

        this.setState({
            edition: edition,
            form: form
        });
    }

    componentWillUnmount() {
        this.state.edition.removeObserver(this);
    }

    startUpdate() {
        const { actions } = this.state;

        actions.isLoadingEdition = true;

        this.setState({
            actions: actions
        });
    }

    endUpdate() {
        const { actions } = this.state;

        actions.isLoadingEdition = false;

        this.setState({
            actions: actions
        });
    }

    updateNetwork() {
        const { form } = this.state;
        
        const clearedForm = {
            layout: form.layout,
            lines: [],
            pointType: form.pointType,
            paths: [],
            points: [],
            referenceDate: form.referenceDate,
            mapPoint: undefined
        };
        
        this.setState({
            form: clearedForm,
            loadingLines: true
        }, () => {
            this.getLines(this.state.edition);
            this.getMapPoints(this.state.edition);
        });
    }

    getLayouts() {
        const { actions, commands } = this.state;
        commands.layouts.list(
            (a) => this.listSuccessCallback(a, "layouts", "isLoadingLayouts"),
            (a) => this.listErrorCallback(a, "layouts", "isLoadingLayouts")
        );

        actions.isLoadingLayouts = true;

        this.setState({
            actions: actions
        });
    }

    isBackDisabled() {
        return this.state.step === 0;
    }

    isNextDisabled() {
        const { step, substep } = this.state;
        const currentStepConfiguration = StepConfigurationHelper.getCurrentStepConfig(step, substep, this.state);

        if (!currentStepConfiguration) {
            return true;
        }

        return currentStepConfiguration.isNextDisabled(this.state);
    }

    onNextClick() {
        const { render, step, substep, actions } = this.state;
        const currentStepConfiguration = StepConfigurationHelper.getCurrentStepConfig(step, substep, this.state);

        render.filter = "";

        if (currentStepConfiguration && currentStepConfiguration.onNext) {
            currentStepConfiguration.onNext(this);
        }

        let newStep = step;
        let newSubstep = substep;

        if (substep === null) {
            // Currently on main step
            if (StepConfigurationHelper.hasAvailableSubsteps(step, this.state)) {
                newSubstep = 0;
            } else {
                newStep = step + 1;
                newSubstep = null;
            }
        } else {
            // Currently on a substep
            const totalSubsteps = StepConfigurationHelper.getSubstepCount(step, this.state);

            if (substep + 1 < totalSubsteps) {
                newSubstep = substep + 1;
            } else {
                newStep = step + 1;
                newSubstep = null;
            }
        }

        // Check if we're trying to download on the last step
        if (newStep >= StepConfigurationHelper.getTotalSteps()) {
            if (!actions.isDownloading) {
                this.onDownloadClick();
            }

            return;
        }

        this.setState({
            step: newStep,
            substep: newSubstep,
            render: render
        });
    }

    onRulerClick(targetStep) {
        const { step, substep } = this.state;

        let currentPosition = step;
        if (substep !== null) {
            currentPosition = step; // Still on same main step even if in substep
        }

        // Don't allow clicking forward
        if (targetStep > currentPosition) {
            return;
        }

        const { render } = this.state;
        render.filter = "";

        this.setState({
            step: targetStep,
            substep: null, // Always go to main step when clicking ruler
            render: render
        });
    }

    onBackClick() {
        const { render, step, substep } = this.state;
        render.filter = "";

        let newStep = step;
        let newSubstep = substep;

        if (substep === null && step > 0) {
            // Go to previous main step
            newStep = step - 1;

            // Check if previous step has substeps
            const prevStepSubstepCount = StepConfigurationHelper.getSubstepCount(newStep, this.state);
            newSubstep = prevStepSubstepCount > 0 ? prevStepSubstepCount - 1 : null
        } else {
            // Currently on a substep (not first), go to previous substep or main step
            newSubstep = substep - 1 > 0 ? substep - 1 : null;
        }

        this.setState({
            step: newStep,
            substep: newSubstep,
            render: render
        });
    }

    onFullReturnClick() {
        const { render, data, form } = this.state;
        render.filter = "";

        const clearedForm = {
            layout: form.layout,
            lines: [],
            pointType: form.pointType,
            paths: [],
            points: [],
            referenceDate: form.referenceDate
        };

        const clearedData = {
            lines: [],
            paths: [],
            points: [],
            layouts: data.layouts
        };

        this.setState({
            form: clearedForm,
            data: clearedData,
            step: 0,
            substep: null,
            render: render,
            usePriorityPaths: false,
            showShapeWarning: false,
            shouldAggregatePaths: false
        }, () => {
            this.getMapPoints(this.state.edition);
            this.getLines(this.state.edition);
        });
    }

    onImportClick() {
        if (this.fileImporterRef.current) {
            this.fileImporterRef.current.openFileDialog();
        }
    }

    onImporterError(err) {
        const { actions } = this.state;

        actions.isImporting = false;

        this.setState({
            actions: actions,
            message: {
                show: true,
                message: "Dados de importação inválidos.",
                style: "danger",
                title: "Erro",
                icon: "remove",
            }
        });
    }

    onExportClick() {
        const { form } = this.state;

        const exportData = {
            layout: form.layout.id,
            lines: form.lines.map(line => line.code),
            pointType: form.pointType,
            paths: form.paths.map(path => {
                return { code: path.code, backColor: path.backColor };
            }),
            points: form.points.map(point => {
                return { name: point.name, code: point.code, isStop: point.isStop, isToExclude: point.isToExclude };
            }),
            usePriorityPaths: this.state.usePriorityPaths,
            shouldAggregatePaths: this.state.shouldAggregatePaths
        }

        const jsonBlob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: "application/json;charset=utf-8"
        });
        const url = URL.createObjectURL(jsonBlob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `export-schematic-config-${Date.now()}.ipubschematicconfig`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }

    onChangeReferenceDateChange(date) {
        const { form } = this.state;

        form.referenceDate = date;

        this.setState({
            form: form,
        }, () => {
            const { edition } = this.state;
            edition.update(new Date(date));
        });
    }

    onSearch(filter) {
        const { render } = this.state;

        render.filter = filter;

        this.setState({
            render: render
        });
    }

    async onExitDialog(isToExecute, argument) {
        const { actions, form } = this.state;

        if (isToExecute) {
            switch (argument.operation) {
                case "proceedImport":
                    actions.isImporting = true;
                    this.continueImport(argument.data, form.layout);
                    break;

                default: // DO NOTHING
                    break;
            }
        } else {
            if (argument && argument.operation === "proceedImport") {
                actions.isImporting = false;
            }
        }

        this.setState({
            actions: actions,
            dialog: {
                show: false,
                title: undefined,
                message: undefined,
                style: undefined,
                icon: undefined,
                argument: undefined
            }
        });
    }

    getLines(edition) {
        return new Promise((resolve, reject) => {
            const { actions, commands } = this.state;

            commands.lines.list(
                edition.network,
                (a) => {
                    this.listSuccessCallback(a, "lines", "isLoadingLines");
                    resolve(a);
                },
                (a) => {
                    this.listErrorCallback(a, "lines", "isLoadingLines");
                    reject(a);
                }
            );

            actions.isLoadingLines = true;

            this.setState({
                actions: actions
            });
        });
    }

    getMapPoints(edition) {
        const { actions, commands }= this.state;
        commands.points.getAllMapPoints(
            edition.network,
            (a) => this.listSuccessCallback(a, "mapPointsFilter", "isLoadingMapPoints"),
            (a) => this.listErrorCallback(a, "mapPointsFilter", "isLoadingMapPoints")
        )

        actions.isLoadingMapPoints = true;

        this.setState({
            actions: actions
        });
    }

    getPaths(lines) {
        return new Promise((resolve, reject) => {
            const { actions, commands, edition } = this.state;

            const batchSize = 10;
            const batches = [];
            for (let i = 0; i < lines.length; i += batchSize) {
                batches.push(lines.slice(i, i + batchSize));
            }

            actions.isLoadingPaths = true;
            actions.remainingBatches = batches.length;

            this.setState({
                actions: actions
            });

            let completedBatches = 0;
            let hasError = false;

            batches.forEach(batch => {
                const ids = batch.map(line => line.id);

                commands.paths.get(
                    edition.network,
                    ids,
                    (a) => {
                        this.listSuccessCallback(a, "paths", "isLoadingPaths", true);
                        completedBatches++;
                        if (completedBatches === batches.length && !hasError) {
                            resolve();
                        }
                    },
                    (a) => {
                        this.listErrorCallback(a, "paths", "isLoadingPaths");
                        hasError = true;
                        reject(a);
                    }
                );
            });
        });
    }

    getPathsShapes(paths) {
        return new Promise((resolve, reject) => {
            const { actions, commands, edition } = this.state;

            const batchSize = 10;
            const batches = [];

            for (let i = 0; i < paths.length; i += batchSize) {
                batches.push(paths.slice(i, i + batchSize));
            }

            actions.isLoadingShapes = true;
            actions.remainingBatches = batches.length;

            this.setState({
                actions: actions
            });

            let completedBatches = 0;
            let hasError = false;

            batches.forEach(batch => {
                const ids = batch.map(paths => paths.id);

                commands.paths.getShapes(
                    edition.network,
                    ids,
                    (a) => {
                        this.listSuccessCallback(a, "shapes", "isLoadingShapes", true);
                        completedBatches++;
                        if (completedBatches === batches.length && !hasError) {
                            resolve();
                        }
                    },
                    (a) => {
                        this.listErrorCallback(a, "shapes", "isLoadingShapes");
                        hasError = true;
                        reject(a);
                    }
                );
            });
        });
    }

    getPoints(paths, pointType) {
        return new Promise((resolve, reject) => {
            const { actions, commands, edition } = this.state;

            const batchSize = 10;
            const batches = [];
            for (let i = 0; i < paths.length; i += batchSize) {
                batches.push(paths.slice(i, i + batchSize));
            }

            actions.isLoadingPoints = true;
            actions.remainingBatches = batches.length;

            this.setState({
                actions: actions
            });

            let completedBatches = 0;
            let hasError = false;

            batches.forEach(batch => {
                const ids = batch.map(path => path.id);

                commands.points.get(
                    edition.network,
                    pointType,
                    ids,
                    (a) => {
                        this.listSuccessCallback(a, "points", "isLoadingPoints", true);
                        completedBatches++;
                        if (completedBatches === batches.length && !hasError) {
                            resolve();
                        }
                    },
                    (a) => {
                        this.listErrorCallback(a, "points", "isLoadingPoints");
                        hasError = true;
                        reject(a);
                    }
                );
            });
        });
    }

    listSuccessCallback(result, dataType, action, inBatches = false) {
        const { data, actions, form } = this.state;

        if (inBatches) {
            if (dataType === "shapes") {
                form.paths = form.paths.map(path => {
                    if (result.hasOwnProperty(path.id)) {
                        return { ...path, shape: result[path.id] };
                    }
                    return path;
                });

                this.checkForMissingShapes(form.paths);
            }
            else if (dataType === "points") {
                result.forEach(newPoint => {
                    const existingIndex = data[dataType].findIndex(
                        p => p.id === newPoint.id && p.isStop === newPoint.isStop
                    );

                    if (existingIndex >= 0) {
                        if (newPoint.isMandatory) {
                            data[dataType][existingIndex].isMandatory = true;
                        }
                        data[dataType][existingIndex].isToExclude = newPoint.isToExclude;
                    } else {
                        data[dataType].push(newPoint);
                    }

                });
            } else {
                result.forEach(newItem => {
                    const exists = data[dataType].some(item => item.id === newItem.id);
                    if (!exists) {
                        data[dataType].push(newItem);
                    }
                });
            }

            actions.remainingBatches -= 1;

            if (actions.remainingBatches === 0) {
                actions[action] = false;
            }
        } else {
            data[dataType] = result;
            actions[action] = false;
        }

        this.setState({
            data: data,
            actions: actions
        });
    }

    listErrorCallback(messageReceived, dataType, action) {
        const { data, actions, message } = this.state;

        actions[action] = false;
        data[dataType] = [];

        if (message.show) {
            messageReceived = `${message.message}\n${messageReceived}`;
        }

        this.setState({
            data: data,
            actions: actions,
            message: {
                show: true,
                message: messageReceived,
                style: "danger",
                title: "Erro",
                icon: "remove"
            }
        });
    }

    async importData(jsonData) {
        if (!this.validateImportData(jsonData)) {
            this.onImporterError("Dados de importação inválidos.");
            return;
        }

        const { layout } = jsonData;
        const { data, actions } = this.state;

        const selectedLayout = data.layouts.find(l => l.id === layout);
        if (undefined === selectedLayout) {
            this.setState({
                dialog: {
                    show: true,
                    title: "Aviso",
                    message: "Layout importado não encontrado. Deseja continuar com o layout selecionado atualmente?",
                    style: "warning",
                    icon: "alert",
                    argument: {
                        operation: "proceedImport",
                        data: jsonData
                    }
                }
            });

            return;
        }

        actions.isImporting = true;
        this.setState({
            actions: actions
        }, async () => {
            await this.continueImport(jsonData, selectedLayout);
        })
    }

    async continueImport(jsonData, layoutToUse = undefined) {
        const { lines, pointType, paths, points, usePriorityPaths, shouldAggregatePaths } = jsonData;
        const { data, form, actions } = this.state;

        let currentStep = 0;
        let keepFirstStep = false;

        if(undefined !== layoutToUse) {
            form.layout = layoutToUse;
        }
        else {
            keepFirstStep = true;
        }

        form.pointType = pointType;

        const importResult = this.importLines(lines, data, form);
        if (importResult.shouldStop) {
            this.finishImport(form, actions, keepFirstStep ? 0 : currentStep, usePriorityPaths, shouldAggregatePaths, importResult.message);
            return;
        }
        currentStep = currentStep + 1;

        await this.getPaths(form.lines);
        const pathsResult = this.importPaths(paths, lines, data, form);
        if (pathsResult.shouldStop) {
            this.finishImport(form, actions, keepFirstStep ? 0 : currentStep, false, shouldAggregatePaths, pathsResult.message);
            return;
        }
        currentStep = currentStep + 2;

        await this.getPathsShapes(form.paths);
        await this.getPoints(form.paths, pointType);
        const pointsResult = this.importPoints(points, lines, paths, data, form);
        if (pointsResult.shouldStop) {
            this.finishImport(form, actions, keepFirstStep ? 0 : currentStep, usePriorityPaths, shouldAggregatePaths, pointsResult.message);
            return;
        }
        currentStep = currentStep + 1;

        const finalMessage = this.getMissingItemsMessage(lines, paths, points);
        this.finishImport(form, actions, keepFirstStep ? 0 : currentStep, usePriorityPaths, shouldAggregatePaths, finalMessage);
    }

    importLines(importedLines, data, form) {
        form.lines = data.lines.filter(line => importedLines.includes(line.code));

        const shouldStop = form.lines.length === 0;
        const message = shouldStop 
            ? this.getMissingItemsMessage(importedLines) 
            : null;

        return { shouldStop, message };
    }

    importPaths(importedPaths, importedLines, data, form) {
        form.paths = data.paths
            .filter(path => importedPaths.some(p => p.code === path.code))
            .map(path => {
                const importedPath = importedPaths.find(p => p.code === path.code);
                return { ...path, backColor: importedPath.backColor };
            });

        const shouldStop = form.paths.length === 0;
        const message = shouldStop
            ? this.getMissingItemsMessage(importedLines, importedPaths)
            : null;

        return { shouldStop, message };
    }

    importPoints(importedPoints, importedLines, importedPaths, data, form) {
        form.points = data.points
            .filter(point => this.matchImportedPoint(point, importedPoints))
            .map(point => {
                const importedPoint = this.findImportedPoint(point, importedPoints);
                return { ...point, isToExclude: importedPoint.isToExclude };
            });

        const shouldStop = form.points.length === 0;
        const message = shouldStop
            ? this.getMissingItemsMessage(importedLines, importedPaths, importedPoints)
            : null;

        return { shouldStop, message };
    }

    finishImport(form, actions, step, usePriorityPaths, shouldAggregatePaths, message) {
        actions.isImporting = false;

        this.setState({
            form,
            step,
            actions,
            usePriorityPaths,
            shouldAggregatePaths,
            ...(message && {
                message: {
                    show: true,
                    message: message,
                    style: "warning",
                    title: "Aviso",
                    icon: "alert",
                }
            })
        });
    }

    matchImportedPoint(point, importedPoints) {
        return importedPoints.some(p =>
            (p.isStop && p.code === point.code) ||
            (!p.isStop && p.name === point.name)
        );
    }

    findImportedPoint(point, importedPoints) {
        return importedPoints.find(p =>
            (p.isStop && p.code === point.code) ||
            (!p.isStop && p.name === point.name)
        );
    }

    getMissingItemsMessage(importedLines = undefined, importedPaths = undefined, importedPoints = undefined) {
        const { form } = this.state;

        
        let missingLines = [];
        if(undefined !== importedLines) {
            missingLines = importedLines.filter(code =>
                !form.lines.some(line => line.code === code)
            );
        }

        let missingPaths = [];
        if(undefined !== importedPaths) {
            missingPaths = importedPaths.filter(p =>
                !form.paths.some(path => path.code === p.code)
            );
        }

        let missingPoints = [];
        if(undefined !== importedPoints) {
            missingPoints = importedPoints.filter(p =>
                !form.points.some(point =>
                    (p.isStop && p.code === point.code) ||
                    (!p.isStop && p.name === point.name)
                )
            );
        }

        const warnings = [];
        if (missingLines.length > 0) {
            warnings.push(`${missingLines.length} linha(s): ${missingLines.join(', ')}`);
        }
        if (missingPaths.length > 0) {
            warnings.push(`${missingPaths.length} percurso(s): ${missingPaths.map(p => p.code).join(', ')}`);
        }
        if (missingPoints.length > 0) {
            warnings.push(`${missingPoints.length} ponto(s): ${missingPoints.map(p => p.code || p.name).join(', ')}`);
        }

        if (warnings.length > 0) {
            return "Os seguintes dados não foram encontrados:\n\n" +
                    warnings.join('\n') + "."
        }

        return "";
    }

    validateImportData(data) {
        if (undefined === data || null == data || typeof data !== 'object') {
            return false;
        }

        const requiredProps = ['layout', 'lines', 'pointType', 'paths', 'points', 'usePriorityPaths', 'shouldAggregatePaths'];
        for (const prop of requiredProps) {
            if (!(prop in data)) {
                return false;
            }
        }

        if (!Array.isArray(data.lines)) {
            return false;
        }

        if (!Array.isArray(data.paths)) {
            return false;
        }

        if (!Array.isArray(data.points)) {
            return false;
        }

        for (let i = 0; i < data.paths.length; i++) {
            const path = data.paths[i];
            if (undefined === path.code || undefined === path.backColor) {
                return false;
            }
        }

        for (let i = 0; i < data.points.length; i++) {
            const point = data.points[i];
            if(undefined === point.isStop) {
                return false;
            }

            if(point.isStop && undefined === point.code) {
                return false;
            } 

            if(!point.isStop && undefined === point.name) {
                return false;
            }

            if (undefined === point.isToExclude) {
                return false;
            }
        }

        if (typeof data.usePriorityPaths !== 'boolean') {
            return false;
        }

        if (typeof data.shouldAggregatePaths !== 'boolean') {
            return false;
        }

        return true;
    }

    checkForMissingShapes(paths) {
        const hasMissingShapes = paths.some(path => undefined === path.shape || path.shape.segments.length === 0);
        this.setState({
            showShapeWarning: hasMissingShapes
        });
    };

    generationSuccessCallback(svg) {
        const { actions, render } = this.state;

        actions.isGenerating = false;
        render.result = `${svg}`.replaceAll("\n", "");

        this.setState({
            actions: actions,
            render: render,
        });
    }

    generationErrorCallback(messageReceived) {
        const { actions, message } = this.state;

        actions.isGenerating = false;

        if (message.show) {
            messageReceived = `${message.message}\n${messageReceived}`;
        }

        this.setState({
            actions: actions,
            message: {
                show: true,
                message: messageReceived,
                style: "danger",
                title: "Erro",
                icon: "remove",
            },
            step: 3
        });
    }

    onRowClick(row, dataType) {
        const { form, data} = this.state;
        let { usePriorityPaths, showShapeWarning, shouldAggregatePaths } = this.state;

        const index = form[dataType].findIndex(data => data.id === row.id);

        if (index === -1) {
            form[dataType].push(row);
        }
        else {
            form[dataType] = form[dataType].slice(0, index).concat(form[dataType].slice(index + 1, form[dataType].length));
        }

        if (dataType === "lines") {
            form.paths = [];
            form.points = [];
            showShapeWarning = false;
            usePriorityPaths = false;
            shouldAggregatePaths = false;
        } else if (dataType === "paths") {
            form.points = [];
            data.points = [];
            showShapeWarning = false;
        }

        this.setState({
            form: form,
            data: data,
            usePriorityPaths: usePriorityPaths,
            shouldAggregatePaths: shouldAggregatePaths,
            showShapeWarning: showShapeWarning
        });
    }

    onSelectAllMandatoryPoints(points) {
        const { form } = this.state;

        const existingIds = new Set(form.points.map(p => p.id));
        const newPoints = points.filter(p => !existingIds.has(p.id));
        form.points = [...form.points, ...newPoints];

        this.setState({
            form: form
        });
    }

    onSelectAll(dataType, filteredData = undefined) {
        const { data, form } = this.state;
        let { usePriorityPaths } = this.state;

        const allItems = filteredData || data[dataType] || [];
        const selectedItems = form[dataType] || [];

        const filteredIds = allItems.map(item => item.id);
        const selectedFilteredItems = selectedItems.filter(item =>
            filteredIds.includes(item.id)
        );

        const allFilteredSelected = selectedFilteredItems.length === allItems.length && allItems.length > 0;
        if (allFilteredSelected) {
            if (dataType === "points") {
                // Keep mandatory points and remove filtered non-mandatory points
                const filteredIdsSet = new Set(filteredIds);
                form[dataType] = selectedItems.filter(p =>
                    p.isMandatory || !filteredIdsSet.has(p.id)
                );
            } else {
                const filteredIdsSet = new Set(filteredIds);
                form[dataType] = selectedItems.filter(item =>
                    !filteredIdsSet.has(item.id)
                );
            }
        } else {
            const existingIds = new Set(selectedItems.map(item => item.id || item));
            const newItems = allItems.filter(item => !existingIds.has(item.id || item));
            form[dataType] = [...selectedItems, ...newItems];
        }

        if (dataType === "lines") {
            form.paths = [];
            form.points = [];
            usePriorityPaths = false;
        } else if (dataType === "paths") {
            form.points = [];
        }

        this.setState({
            form: form,
            usePriorityPaths: usePriorityPaths
        });
    }
    
    onSelectPriorityPaths(e) {
        const { form, data } = this.state;

        if (e) {
            const priorityPaths = [];
            form.lines.forEach(line => {
                if (Array.isArray(line.priorityPathIds)) {
                    line.priorityPathIds.forEach(priorityPathId => {
                        const match = data.paths.find(p => p.id === priorityPathId);
                        if (match && !priorityPaths.some(path => path.id === match.id)) {
                            priorityPaths.push(match);
                        }
                    });
                }
            });

            form.paths = priorityPaths;
        } else {
            form.paths = [];
            form.points = [];
        }

        form.points = [];

        this.setState({
            form: form,
            usePriorityPaths: e
        });
    }

    onSelectValue(value, dataType) {
        const { form, data } = this.state;

        form[dataType] = value;

        if("layout" !== dataType) {
            if ("mapPoint" === dataType) {
                form.lines = [];
            }

            form.points = [];
            data.points = [];
        }

        this.setState({
            form: form,
            data: data
        });
    }

    onExitMessage() {
        this.setState({
            message: {
                show: false,
                title: undefined,
                message: undefined,
                style: undefined,
                icon: undefined
            }
        });
    }

    onDownloadClick() {
        const { render, actions } = this.state;

        // This is not my proudest moment but it works...
        actions.isDownloading = true;
        this.setState({
            actions: actions
        }, () => {
            const svgBlob = new Blob([render.result], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `mapa-esquematico-${Date.now()}.svg`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

            actions.isDownloading = false;
            this.setState({
                actions: actions
            });
        })
    }
    
    onPathsChange(newPaths) {
        this.setState(prev => ({
            form: {
                ...prev.form,
                paths: [...newPaths]
            }
        }));
    }

    onColorsChange(colors) {
        const { lineColors, pathColors } = colors;
        const { form } = this.state;

        const updatedLines = form.lines.map(line => {
            if (lineColors[line.id]) {
                return { ...line, backColor: lineColors[line.id] };
            }
            return line;
        });

        const updatedPaths = form.paths.map(path => {
            if (pathColors[path.id]) {
                return { ...path, backColor: pathColors[path.id] };
            }
            return path;
        });


        this.setState({
            form: {
                ...form,
                lines: updatedLines,
                paths: updatedPaths
            }
        });
    }

    generateMap() {
        const { actions, commands, edition, form, shouldAggregatePaths, useAllLinesInCaption } = this.state;

        actions.isGenerating = true;

        const data = {
            layoutId: undefined === form.layout ? 0 : form.layout.id,
            Paths: form.paths.map(({ id, backColor }) => ({ id, backColor })),
            Points: form.points.map(({ id, isMandatory, isStop, isToExclude }) => ({
                id,
                isMandatory,
                isStop,
                isToExclude
            })),
            AggregatePaths: shouldAggregatePaths,
            ReferenceDate: form.referenceDate,
            UseAllLines: useAllLinesInCaption
        };

        commands.draw.generate(
            edition.network,
            data,
            (a) => this.generationSuccessCallback(a),
            (a) => this.generationErrorCallback(a)
        );

        this.setState({
            actions: actions
        });
    }

    isRowSelected(row, dataType) {
        const { form } = this.state;
        const selected = form[dataType] || [];

        return selected.some(data => data.id === row.id);
    }

    renderLineSelection() {
        const { form, data, render, actions } = this.state;

        if(actions.isLoadingLines || actions.isLoadingMapPoints) {
            return <LoadingSpinner className="loading schematicmap-loading" />;
        }

        let linesFiltered = SearchHelper.search({
            filter: render.filter,
            data: data.lines,
            fields: ["code", "extCode", "goName", "returnName"]
        });

        if(undefined !== form.mapPoint && null !== form.mapPoint) {
            linesFiltered = linesFiltered.filter(line => line.mapPointIds.includes(form.mapPoint.id));
        }

        return <LineSelection
            referenceDate={form.referenceDate}
            layouts={data.layouts}
            selectedLayout={form.layout}
            mapPoints={data.mapPointsFilter}
            selectedMapPoint={form.mapPoint}
            lines={linesFiltered}
            pointType={form.pointType}
            onSearch={(filter) => this.onSearch(filter)}
            onRowClick={(row) => this.onRowClick(row, "lines")}
            isRowSelected={(row) => this.isRowSelected(row, "lines")}
            onChangeReferenceDateChange={(e) => this.onChangeReferenceDateChange(e)}
            onSelectValue={(a, b) => this.onSelectValue(a,b)}
            onSelectAll={() => this.onSelectAll("lines", linesFiltered)}
        />;
    }

    renderPathSelection() {
        const { form, data, actions, usePriorityPaths, shouldAggregatePaths } = this.state;

        if(actions.isLoadingPaths) {
            return <LoadingSpinner className="loading schematicmap-loading" />;
        }

        return <PathSelection
            paths={data.paths}
            lines={form.lines}
            selectedPaths={form.paths}
            onPathsChange={(paths) => {this.onPathsChange(paths)}}
            usePriorityPaths={usePriorityPaths}
            shouldAggregatePaths={shouldAggregatePaths}
            onSearch={(filter) => this.onSearch(filter)}
            onRowClick={(row) => this.onRowClick(row, "paths")}
            isRowSelected={(row) => this.isRowSelected(row, "paths")}
            onAggregatePathsToggle={() => this.setState(prev => ({ shouldAggregatePaths: !prev.shouldAggregatePaths }))}
            onSelectPriorityPaths={(e) => this.onSelectPriorityPaths(e)} />;
    }

    renderPointSelection() {
        const { form, data, actions, render } = this.state;

        if (actions.isLoadingPoints) {
            return <LoadingSpinner className="loading schematicmap-loading" />;
        }

        let pointsFiltered = SearchHelper.search({
            filter: render.filter,
            data: data.points.filter(p => !p.isMandatory),
            fields: ["code", "name" ]
        });

        return <PointSelection
            points={pointsFiltered}
            mandatoryPoints={data.points.filter(p => p.isMandatory)}
            pointType={form.pointType}
            onSearch={(filter) => this.onSearch(filter)}
            onRowClick={(row) => this.onRowClick(row, "points")}
            isRowSelected={(row) => this.isRowSelected(row, "points")}
            onSelectAllMandatoryPoints={(points) => this.onSelectAllMandatoryPoints(points)}
            onSelectAll={() => this.onSelectAll("points", pointsFiltered)}/>
    }

    renderPointCaptionSelection() {
        const { form, render, useAllLinesInCaption } = this.state;

        let pointsFiltered = SearchHelper.search({
            filter: render.filter,
            data: form.points.filter(p => p.isStop === false),
            fields: ["name"]
        });

        return (
            <div style={{ margin: "10px", overflowX: "clip" }}>
                <div style={{ margin: "10px" }}>
                    <div className={`schematicmap-option ${useAllLinesInCaption ? "active" : ""}`} onClick={() => this.setState({ useAllLinesInCaption: !useAllLinesInCaption })} >
                        <input
                            type="checkbox"
                            checked={useAllLinesInCaption}
                            onChange={() => this.setState({ useAllLinesInCaption: !useAllLinesInCaption})}
                            className="schematicmap-checkbox"
                        />
                        <span className="schematicmap-label">Mostrar linhas ausentes do mapa</span>
                    </div>
                </div>
                <Row>
                    <Col md={12}>
                        <Panel bsStyle="default">
                            <Panel.Heading>
                                <h4 className="panel-title">Selecionar Pontos para a Legenda</h4>
                            </Panel.Heading>
                            <Panel.Body>
                                <GenericSelectableTable
                                    data={pointsFiltered}
                                    onSearch={(filter) => this.onSearch(filter)}
                                    onRowClick={(row) => {
                                        const point = pointsFiltered.find(p => p.id === row.id);
                                        
                                        if (point) {
                                            point.isToExclude = !point.isToExclude;
                                            this.setState({ form: form });
                                        }
                                    }}
                                    isRowSelected={(row) =>  {
                                        const point = pointsFiltered.find(p => p.id === row.id);
                                        return point ? point.isToExclude === false : false;
                                    }}
                                    renderCell={(row) => {
                                        return `${row.code !== undefined ? `${row.code} | ${row.name}` : row.name}`;
                                    }}
                                    onSelectAll={() => {
                                        const allExcluded = pointsFiltered.every(p => p.isToExclude === true);

                                        form.points.filter(p => p.isStop === false).forEach(p => {
                                            p.isToExclude = allExcluded ? false : true;
                                        });
                                        
                                        this.setState({ form: form });
                                    }}
                                    sortableFields={[
                                        { value: "name", label: "Nome" },
                                    ]}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            </div>
        );
    }

    renderLineColorSelection() {
        const { form } = this.state;

        return (
            <LineColorSelection
                lines={form.lines}
                paths={form.paths}
                onColorsChange={(colors) => this.onColorsChange(colors)}
            />
        );
    }

    renderSummary() {
        const { form, actions } = this.state;

        if (actions.isLoadingShapes) {
            return <LoadingSpinner className="loading schematicmap-loading" />;
        }

        return <MapSummary points={form.points} paths={form.paths} showShapeWarning={form.showShapeWarning} />;
    }

    renderResult() {
        const { render, actions } = this.state;

        if(undefined === render.result || actions.isGenerating) {
            return <LoadingSpinner className="loading schematicmap-loading" />;
        }

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    padding: "20px",
                    boxSizing: "border-box",
                }}
            >
                <div
                    style={{
                        border: "2px solid #ccc",
                        borderRadius: "12px",
                        padding: "16px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                        maxWidth: "90%",
                        overflow: "auto",
                    }}
                >
                    <img
                        src={`data:image/svg+xml;utf8,${encodeURIComponent(render.result)}`}
                        alt="SVG"
                        style={{
                            display: "block",
                            maxWidth: "100%",
                            maxHeight: "80vh",
                            margin: "0 auto",
                        }}
                    />
                </div>
            </div>
        );
    }

    renderContent() {
        const { step, substep } = this.state;
        const currentStepConfiguration = StepConfigurationHelper.getCurrentStepConfig(step, substep, this.state);

        if (!currentStepConfiguration || !currentStepConfiguration.render) {
            return null;
        }

        return this[currentStepConfiguration.render]();
    }

    renderLoadingFeedback(isLoading) {
        return (
            <HandleMessage
                title="A Carregar a Edição..."
                message={<LoadingSpinner />}
                style={`primary`}
                icon="transfer"
                show={isLoading}
                small
                noButtons />
        );
    }

    renderGeneratingFeedback(isGenerating) {
        return (
            <HandleMessage
                title="A Gerar..."
                message={<LoadingSpinner />}
                style={`primary`}
                icon="transfer"
                show={isGenerating}
                small
                noButtons />
        );
    }

    renderImportingFeedback(isImporting) {
        return (
            <HandleMessage
                title="A Importar..."
                message={<LoadingSpinner />}
                style={`primary`}
                icon="transfer"
                show={isImporting}
                small
                noButtons />
        );
    }

    renderStepRuler() {
        const { step, substep } = this.state;
        const rulerSteps = StepConfigurationHelper.getRulerSteps();

        const elements = [];

        elements.push(
            <div
                key="arrow-left"
                className={`arrow left ${!this.isBackDisabled() ? "active" : ""}`}
                onClick={() => !this.isBackDisabled() && this.onBackClick()}
            />
        );

        rulerSteps.forEach((rulerStep, index) => {
            const isActive = StepConfigurationHelper.isMainStepActive(index, step);
            const isCompleted = StepConfigurationHelper.isMainStepCompleted(index, step);

            if (index > 0) {
                elements.push(
                    <div
                        key={`line-${index}`}
                        className={`line ${isCompleted || isActive ? "active" : ""}`}
                    />
                );
            }

            // Check if this step has available substeps
            const hasSubsteps = StepConfigurationHelper.hasAvailableSubsteps(index, this.state);
            const substepCount = StepConfigurationHelper.getSubstepCount(index, this.state);

            elements.push(
                <div
                    key={`step-${rulerStep.id}`}
                    className={`step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                    onClick={() => this.onRulerClick(index)}
                >
                    <div className="circle">{index + 1}</div>
                    <div className="label">{rulerStep.label}</div>
                    <div className="substep-indicator dots">
                        {hasSubsteps ? (
                            Array.from({ length: substepCount }).map((_, i) => (
                                <div key={i} className={`dot ${i === substep ? 'active' : ''}`} />
                            ))
                        ) : null}
                    </div>
                </div>
            );
        });

        elements.push(
            <div
                key="arrow-right"
                className={`arrow right ${!this.isNextDisabled() ? "active" : ""}`}
                onClick={() => !this.isNextDisabled() && this.onNextClick()}
            />
        );

        return (
            <div className="schematicmap-step-ruler">
                {elements}
            </div>
        );
    }

    render() {
        const { message, step, substep, actions, dialog } = this.state;

        const isOnLastMainStep = step === StepConfigurationHelper.getTotalSteps() - 1;
        const substepCount = StepConfigurationHelper.getSubstepCount(step, this.state);
        const isOnLastSubstep = substep === null || substep === substepCount - 1;
        const isLastStep = isOnLastMainStep && isOnLastSubstep;
        const isOnFirstStep = step === 0;

        const isSecondToLast = (isOnLastMainStep && substep !== null && substep === substepCount - 2) ||
            (step === StepConfigurationHelper.getTotalSteps() - 2 &&
                StepConfigurationHelper.getSubstepCount(step, this.state) === 0);

        return (
            <div>
                <div className="sticky-header">
                    <TitleRow title="Mapas Esquemáticos de Rede" showSeparator={true}>
                        <Col className="padding-0">
                            <ButtonToolbar className="pull-right">
                                {isLastStep ? (
                                    <Button onClick={() => this.onFullReturnClick()}>
                                        VOLTAR AO INICIO
                                    </Button>
                                ) : null}
                                {isOnFirstStep || isSecondToLast ? (
                                    <ButtonGroup>
                                        <FileImporter
                                            ref={this.fileImporterRef}
                                            onFileLoaded={(e) => this.importData(e)}
                                            onError={(err) => this.onImporterError(err)}
                                        />
                                        <Button onClick={() => isOnFirstStep ? this.onImportClick() : this.onExportClick()} disabled={actions.isLoadingLines}>
                                            {isOnFirstStep ? "IMPORTAR" : "EXPORTAR"}
                                        </Button>
                                    </ButtonGroup>
                                ) : null}
                                <Button
                                    onClick={() => this.onBackClick()}
                                    disabled={this.isBackDisabled()}
                                >
                                    RETROCEDER
                                </Button>
                                <Button
                                    onClick={() => this.onNextClick()}
                                    disabled={this.isNextDisabled()}
                                >
                                    {isSecondToLast ? "GERAR" :
                                        isLastStep ? "DOWNLOAD" :
                                            "AVANÇAR"}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </TitleRow>
                </div>

                {this.renderStepRuler()}
                {this.renderContent()}

                {this.renderLoadingFeedback(actions.isLoadingEdition)}
                {this.renderGeneratingFeedback(actions.isGenerating)}
                {this.renderImportingFeedback(actions.isImporting)}


                <HandleMessage
                    title={message.title}
                    message={message.message}
                    style={message.style}
                    icon={message.icon}
                    show={message.show}
                    onHide={() => this.onExitMessage()} />

                <HandleDialog
                    show={dialog.show}
                    title={dialog.title}
                    message={dialog.message}
                    style={dialog.style}
                    icon={dialog.icon}
                    argument={dialog.argument}
                    onExit={(a, b) => this.onExitDialog(a, b)} />
            </div>
        );
    }
}